// The Programme facts shaped from the programme_* rows (live.ts reads them).
// Pure, so it is tested without a database (shape.test.mjs).

import type { Ask, CatchupProject, PlanStep, ReportRef, Task, TaskStatus } from './catchup'

export interface Programme {
  projects: CatchupProject[]
  /** Every live task, column order, then due date, then the order set. */
  tasks: Task[]
  stages: string[]
  asks: Ask[]
  plan: PlanStep[]
  reports: ReportRef[]
  /** Ids of reports about the whole programme, shown on every project as well. */
  programmeReports: string[]
  /** The date of the last edit by a person, else the newest update; null when neither. */
  asOf: string | null
}

type Row = Record<string, unknown>

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null)

/** Shape the database rows into what the pages show. Pure, so it is tested without a database. */
export function shapeProgramme(rows: {
  projects: Row[]
  stages: Row[]
  updates: Row[]
  asks: Row[]
  plan: Row[]
  reports: Row[]
  repos: Row[]
  changes: Row[]
  tasks?: Row[]
}): Programme {
  const live = (r: Row) => !r.archived_at
  const stagesSorted = rows.stages.slice().sort((a, b) => Number(a.sort) - Number(b.sort))
  const stageLabel = new Map(stagesSorted.map((s) => [String(s.key), String(s.label)]))
  const reports: ReportRef[] = rows.reports.filter(live).map((r) => ({
    id: String(r.id),
    title: String(r.title),
    kind: String(r.kind),
    on: String(r.on_date),
    pages: Number(r.pages ?? 0),
    line: str(r.line) ?? '',
    readers: str(r.readers) ?? 'the team',
    project: str(r.project_slug),
    builtin: str(r.builtin),
    filePath: str(r.file_path),
  }))
  const updates = rows.updates.filter(live)
  const projects: CatchupProject[] = rows.projects
    .filter(live)
    .slice()
    .sort((a, b) => Number(a.sort) - Number(b.sort) || String(a.name).localeCompare(String(b.name)))
    .map((p) => {
      const slug = String(p.slug)
      return {
        slug,
        app: str(p.front_door_app) ?? slug,
        name: String(p.name),
        lead: str(p.lead_name) ?? 'To be named',
        leadEmail: str(p.lead_email)?.toLowerCase() ?? null,
        replaces: str(p.replaces),
        what: str(p.what_text) ?? '',
        stage: stageLabel.get(String(p.stage)) ?? String(p.stage ?? ''),
        next: str(p.next_step) ?? 'Not set yet',
        updates: updates
          .filter((u) => u.project_slug === slug)
          .map((u) => ({ on: String(u.on_date), text: String(u.text) }))
          .sort((a, b) => b.on.localeCompare(a.on)),
        waitingOn: Array.isArray(p.waiting_on) ? (p.waiting_on as string[]) : [],
        reports: reports.filter((r) => r.project === slug).map((r) => r.id),
        repo: (rows.repos.find((r) => live(r) && r.project_slug === slug)?.repo as string | undefined) ?? null,
      }
    })
  const asks: Ask[] = rows.asks
    .filter(live)
    .slice()
    .sort((a, b) => Number(a.sort) - Number(b.sort))
    .map((a) => ({
      key: String(a.key),
      no: String(a.sort ?? '').padStart(2, '0'),
      title: str(a.title) ?? String(a.text ?? ''),
      detail: str(a.detail) ?? '',
      from: str(a.owner) ?? 'Nateva',
      askedOn: str(a.asked_on) ?? str(a.due_on) ?? '',
      status: a.status === 'done' ? 'done' : 'waiting',
    }))
  const plan: PlanStep[] = rows.plan
    .filter(live)
    .slice()
    .sort((a, b) => Number(a.sort) - Number(b.sort))
    .map((s) => ({
      from: String(s.starts_on),
      when: String(s.label),
      title: String(s.title),
      items: Array.isArray(s.items) && s.items.length ? (s.items as string[]) : (str(s.detail)?.split('; ') ?? []),
    }))
  const liveSlugs = new Set(projects.map((p) => p.slug))
  const lastEdit = rows.changes
    .filter((c) => str(c.signed_in_email) && (!c.project_slug || liveSlugs.has(String(c.project_slug))))
    .map((c) => String(c.at).slice(0, 10))
    .sort()
    .pop()
  const lastUpdate = projects.flatMap((p) => p.updates.map((u) => u.on)).sort().pop()
  const tasks: Task[] = (rows.tasks ?? [])
    .filter(live)
    .filter((t) => !t.project_slug || liveSlugs.has(String(t.project_slug)))
    .slice()
    .sort(
      (a, b) =>
        COLUMNS.indexOf(a.status as TaskStatus) - COLUMNS.indexOf(b.status as TaskStatus) ||
        (a.status === 'done' ? String(b.done_on ?? '').localeCompare(String(a.done_on ?? '')) : 0) ||
        String(a.due_on ?? '9999').localeCompare(String(b.due_on ?? '9999')) ||
        Number(a.sort) - Number(b.sort),
    )
    .map((t) => ({
      id: String(t.id),
      no: Number(t.no),
      project: str(t.project_slug),
      title: String(t.title),
      detail: str(t.detail),
      status: (COLUMNS.includes(t.status as TaskStatus) ? t.status : 'todo') as TaskStatus,
      owner: str(t.owner_name) ?? 'Not given yet',
      side: t.owner_side === 'nateva' ? 'nateva' : 'edna',
      waitingOn: str(t.waiting_on),
      due: str(t.due_on),
      started: str(t.started_on),
      done: str(t.done_on),
    }))
  return {
    projects,
    tasks,
    stages: stagesSorted.map((s) => String(s.label)),
    asks,
    plan,
    reports,
    programmeReports: reports.filter((r) => !r.project).map((r) => r.id),
    asOf: lastEdit ?? lastUpdate ?? null,
  }
}

/** The board's columns, left to right. */
export const COLUMNS: TaskStatus[] = ['todo', 'doing', 'waiting', 'done']

/** Done work stays on the board this many days. */
export const DONE_DAYS = 14

/** Whole days from today (YYYY-MM-DD) to a date; negative is past. */
const daysTo = (todayIso: string, iso: string) => Math.round((Date.parse(iso.slice(0, 10)) - Date.parse(todayIso)) / 864e5)

export const isOverdue = (t: Task, todayIso: string) => t.status !== 'done' && !!t.due && daysTo(todayIso, t.due) < 0

/** Done in the last DONE_DAYS days, so it still shows on the board. */
export const isRecentDone = (t: Task, todayIso: string) => t.status === 'done' && !!t.done && daysTo(todayIso, t.done) > -DONE_DAYS

export interface BoardLine {
  /** A project slug, or null for the whole programme. */
  project: string | null
  todo: number
  doing: number
  waiting: number
  /** Done in the last DONE_DAYS days. */
  done: number
  overdue: number
  /** The most recent day anything started or finished. */
  lastMoved: string | null
}

/** One line per project, then the whole programme if it has work: the management summary above the board. */
export function boardLines(p: Programme, todayIso: string): BoardLine[] {
  const line = (project: string | null): BoardLine => {
    const ts = p.tasks.filter((t) => t.project === project)
    return {
      project,
      todo: ts.filter((t) => t.status === 'todo').length,
      doing: ts.filter((t) => t.status === 'doing').length,
      waiting: ts.filter((t) => t.status === 'waiting').length,
      done: ts.filter((t) => isRecentDone(t, todayIso)).length,
      overdue: ts.filter((t) => isOverdue(t, todayIso)).length,
      lastMoved: ts.flatMap((t) => [t.started, t.done]).filter((d): d is string => !!d).sort().pop() ?? null,
    }
  }
  const lines = p.projects.map((pr) => line(pr.slug))
  return p.tasks.some((t) => !t.project) ? [...lines, line(null)] : lines
}

export const reportIn =(p: Programme, id: string) => p.reports.find((r) => r.id === id)
export const projectIn = (p: Programme, slug: string) => p.projects.find((x) => x.slug === slug)
export const askIn = (p: Programme, key: string) => p.asks.find((a) => a.key === key)
